import { StyleSheet, css } from 'aphrodite'
import numbro from 'numbro'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTagContext } from '../contexts/TagContext'
import type { LoadResponse } from '../services/loadService'
import { loadService } from '../services/loadService'

interface LoadTableProps {
  tagId: string
  hideEditColumn?: boolean
}

const LoadTable: React.FC<LoadTableProps> = ({
  tagId,
  hideEditColumn = false,
}) => {
  const [loads, setLoads] = useState<LoadResponse[]>([])
  const navigate = useNavigate()
  const { getCurrentTag } = useTagContext()
  const currentTag = getCurrentTag(tagId)
  const isSigned = currentTag?.status === 'SIGNED'
  const isComplete = currentTag?.status === 'COMPLETE'
  const tableRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollability = useCallback(() => {
    const container = tableRef.current
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
    }
  }, [])

  const scrollTable = useCallback((direction: 'left' | 'right') => {
    const container = tableRef.current
    if (container) {
      const scrollAmount = 150
      const newScrollLeft =
        direction === 'left'
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })
    }
  }, [])

  useEffect(() => {
    const fetchLoads = async () => {
      try {
        const response = await loadService.getLoadsByTag(tagId)
        // Sort loads by load number in ascending order
        const sortedLoads = response.sort((a, b) => a.number - b.number)
        setLoads(sortedLoads)
      } catch (error) {
        console.error('Failed to fetch loads:', error)
      }
    }

    fetchLoads()
  }, [tagId])

  useEffect(() => {
    checkScrollability()
    const timeoutId = setTimeout(checkScrollability, 100)
    const container = tableRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollability)
      window.addEventListener('resize', checkScrollability)
      return () => {
        clearTimeout(timeoutId)
        container.removeEventListener('scroll', checkScrollability)
        window.removeEventListener('resize', checkScrollability)
      }
    }
    return () => clearTimeout(timeoutId)
  }, [checkScrollability])

  const handleEditLoad = (loadNumber: number, loadId: number) => {
    navigate(`/tags/open/${tagId}/${loadNumber}/details`, {
      state: { loadId },
    })
  }

  const TableCell: React.FC<{ content: string | number }> = ({ content }) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [hasOverflow, setHasOverflow] = useState(false)

    return (
      <td className={css(styles.td)}>
        <button
          ref={buttonRef}
          className={css(
            styles.tableCellButton,
            isExpanded && styles.expandedCell,
          )}
          onClick={() => {
            setIsExpanded(!isExpanded)
          }}
          style={{ cursor: hasOverflow ? 'pointer' : 'default' }}
        >
          {content}
        </button>
      </td>
    )
  }

  return (
    <div className={css(styles.wrapper)}>
      {canScrollLeft && (
        <>
          <div className={css(styles.scrollIndicatorLeft)} />
          <button
            type="button"
            className={css(styles.scrollArrowLeft)}
            onClick={() => scrollTable('left')}
            aria-label="Scroll left"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Scroll left</title>
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}
      {canScrollRight && (
        <>
          <div className={css(styles.scrollIndicatorRight)} />
          <button
            type="button"
            className={css(styles.scrollArrowRight)}
            onClick={() => scrollTable('right')}
            aria-label="Scroll right"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Scroll right</title>
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}
      <div className={css(styles.tableWrapper)} ref={tableRef}>
        <table className={css(styles.table)}>
          <thead>
            <tr className={css(styles.headerRow)}>
              {!hideEditColumn && !isComplete && (
                <th className={css(styles.headerCell, styles.loadNumberCell)} />
              )}
              <th className={css(styles.headerCell, styles.loadNumberCell)}>
                Load No
              </th>
              <th className={css(styles.headerCell)}>Scale ID</th>
              <th className={css(styles.headerCell)}>Material</th>
              <th className={css(styles.headerCell)}>Weight</th>
              <th className={css(styles.headerCell)}>
                Load
                <br />
                Arr
                <br />
                Time
              </th>
              <th className={css(styles.headerCell)}>
                Load
                <br />
                Dep
                <br />
                Time
              </th>
              <th className={css(styles.headerCell)}>
                Unload
                <br />
                Arr
                <br />
                Time
              </th>
              <th className={css(styles.headerCell)}>
                Unload
                <br />
                Dep
                <br />
                Time
              </th>
              <th className={css(styles.headerCell)}>Tolls</th>
            </tr>
          </thead>
          <tbody>
            {loads.map((load) => (
              <tr key={load.id} className={css(styles.tableRow)}>
                {!hideEditColumn && !isComplete && (
                  <td className={css(styles.tableCell, styles.loadNumberCell)}>
                    <button
                      className={css(styles.editButton)}
                      onClick={() => handleEditLoad(load.number, load.id)}
                    >
                      <i
                        className={`fa-solid ${
                          isSigned ? 'fa-eye' : 'fa-pencil'
                        }`}
                      />
                    </button>
                  </td>
                )}
                <TableCell content={load.number} />
                <TableCell content={load.scale_tag_id || ''} />
                <TableCell content={load.material || ''} />
                <TableCell
                  content={numbro(load.weight).format({
                    mantissa: 2,
                    trimMantissa: true,
                  })}
                />
                <TableCell content={formatTime(load.arrival_time) || '---'} />
                <TableCell content={formatTime(load.departure_time) || '---'} />
                <TableCell
                  content={formatTime(load.unload_arrival_time) || '---'}
                />
                <TableCell
                  content={formatTime(load.unload_departure_time) || '---'}
                />
                <TableCell content={`$${load.tolls || '0.00'}`} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative' as const,
    width: '100%',
  },
  scrollIndicatorLeft: {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: 30,
    background:
      'linear-gradient(to right, rgba(255,255,255,0.95), transparent)',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  scrollIndicatorRight: {
    position: 'absolute' as const,
    right: 0,
    top: 0,
    bottom: 0,
    width: 30,
    background: 'linear-gradient(to left, rgba(255,255,255,0.95), transparent)',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  scrollArrowLeft: {
    position: 'absolute' as const,
    left: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
    },
  },
  scrollArrowRight: {
    position: 'absolute' as const,
    right: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
    },
  },
  card: {
    background: '#FFFFFF',
    border: '1px solid #F3F4F6',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.08)',
    borderRadius: '8px',
    margin: '24px 0',
    overflow: 'hidden',
  },
  tableContainer: {
    padding: '0',
    overflowX: 'auto',
  },
  tableHeader: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111928',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderSpacing: -1,
    overflow: 'hidden',
  },
  headerRow: {
    backgroundColor: '#1C64F2',
    borderBottom: 'none',
  },
  headerCell: {
    padding: '4px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#FFFFFF',
    fontFamily: 'Inter',
    backgroundColor: '#1C64F2',
    border: '0.25px solid #D1D5DB',
    width: '80px',
    whiteSpace: 'normal',
    height: '36px',
    '@media (max-width: 1024px)': {
      maxWidth: '60px',
    },
  },
  headerCellButton: {
    padding: '4px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#FFFFFF',
    fontFamily: 'Inter',
    width: '100%',
    background: 'none',
    border: 'none',
    whiteSpace: 'normal',
    '@media (max-width: 1024px)': {
      maxWidth: '60px',
    },
  },
  loadNumberCell: {
    width: '18px',
  },
  tableRow: {
    borderBottom: '1px solid #E5E7EB',
    ':last-child': {
      borderBottom: 'none',
    },
  },
  tableCell: {
    padding: '4px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#111928',
    fontFamily: 'Inter',
    border: '0.25px solid #D1D5DB',
    whiteSpace: 'nowrap',
    '@media (max-width: 1024px)': {
      maxWidth: '60px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  editButton: {
    background: 'none',
    border: 'none',
    color: '#1C64F2',
    cursor: 'pointer',
    padding: 0,
  },
  tableWrapper: {
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
  },
  expandedCell: {
    '@media (max-width: 1024px)': {
      maxWidth: 'none !important',
      whiteSpace: 'normal',
      overflow: 'visible',
      zIndex: 1,
      position: 'relative',
    },
  },
  td: {
    border: '0.25px solid #D1D5DB',
  },
  tableCellButton: {
    padding: '4px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#111928',
    fontFamily: 'Inter',
    whiteSpace: 'nowrap',
    width: '100%',
    background: 'none',
    // border: "none",
    '@media (max-width: 1024px)': {
      maxWidth: '60px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
})

const formatTime = (timestamp: string | undefined | null) => {
  if (!timestamp) return '---'
  try {
    const date = new Date(timestamp)
    const hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12
    return `${displayHours}:${minutes} ${period}`
  } catch (error) {
    return '---'
  }
}

export default LoadTable
